import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Upload,
  CloudUpload,
  Error as ErrorIcon,
  Visibility,
  Download,
  Analytics,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { customerService } from '../services/customerService';

interface CustomerRecord {
  id?: string;
  name: string;
  phone: string;
  email: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: number;
  last_service?: string;
  preferred_contact: 'whatsapp' | 'sms' | 'email';
  status: 'pending' | 'validated' | 'error';
  errors?: string[];
}

interface UploadBatch {
  id: string;
  filename: string;
  uploaded_at: string;
  total_records: number;
  validated_records: number;
  error_records: number;
  status: 'processing' | 'completed' | 'failed';
  processing_progress: number;
}

const CustomerUpload: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [uploadBatches, setUploadBatches] = useState<UploadBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [uploadConfig, setUploadConfig] = useState({
    skipDuplicates: true,
    validatePhones: true,
    defaultContactMethod: 'whatsapp' as 'whatsapp' | 'sms' | 'email',
    enableGDPRConsent: true,
  });

  const steps = ['Upload File', 'Validate Data', 'Configure Settings', 'Process & Import'];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setUploadedFile(file);
    setLoading(true);
    setError(null);

    try {
      // Parse CSV file for Mooneys Hyundai format
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        throw new Error('File must contain at least a header row and one data row');
      }

      // Get actual CSV headers
      const actualHeaders = lines[0].split(',').map(h => h.trim());

      // Check for required fields
      const requiredFields = ['Customer Name', 'Email', 'Phone'];
      const missingFields = requiredFields.filter(field =>
        !actualHeaders.includes(field)
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required CSV fields: ${missingFields.join(', ')}`);
      }

      const parsedCustomers: CustomerRecord[] = [];
      let validCount = 0;
      let errorCount = 0;

      // Parse each data row
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));

        if (values.length < actualHeaders.length) continue;

        // Create record object using actual header positions
        const rowData: { [key: string]: string } = {};
        actualHeaders.forEach((header, index) => {
          rowData[header] = values[index] || '';
        });

        const customer: CustomerRecord = {
          name: rowData['Customer Name'] || '',
          phone: rowData['Phone'] || '',
          email: rowData['Email'] || '',
          vehicle_make: '', // Will be parsed from Vehicle Model
          vehicle_model: rowData['Vehicle Model'] || '',
          vehicle_year: parseInt(rowData['Vehicle Year']) || undefined,
          last_service: undefined, // Not in Mooneys format
          preferred_contact: 'whatsapp',
          status: 'pending',
          errors: [],
        };

        // Parse vehicle make from model (same logic as backend)
        if (customer.vehicle_model) {
          const makes = ["Hyundai", "Toyota", "Honda", "Ford", "BMW", "Mercedes", "Audi", "Volkswagen"];
          for (const make of makes) {
            if (customer.vehicle_model.toLowerCase().includes(make.toLowerCase())) {
              customer.vehicle_make = make;
              customer.vehicle_model = customer.vehicle_model.replace(make, '').trim();
              break;
            }
          }
        }

        // Sanitize phone number
        if (customer.phone) {
          // Remove extensions (x, ext, extension followed by numbers)
          const phoneWithoutExt = customer.phone.split(/x|ext|extension/i)[0].trim();

          // Basic cleaning - keep digits and common international prefixes
          let cleanedPhone = phoneWithoutExt
            .replace(/[\s\-\.\(\)]/g, ''); // Remove spaces, dashes, dots, parentheses

          // Ensure it starts with + for international format if it has a country code
          if (cleanedPhone.startsWith('00')) {
            cleanedPhone = '+' + cleanedPhone.substring(2);
          }

          // If it's a UK number starting with 0, add country code
          if (cleanedPhone.match(/^0[1-9]/)) {
            cleanedPhone = '+44' + cleanedPhone.substring(1);
          }

          customer.phone = cleanedPhone;
        }

        // Validation
        const errors: string[] = [];
        if (!customer.name) {
          errors.push('Missing customer name');
        }
        if (!customer.phone && !customer.email) {
          errors.push('No valid phone or email');
        }

        // Phone validation - basic format check
        if (customer.phone) {
          const digitsOnly = customer.phone.replace(/\D/g, '');

          // Basic phone number validation
          if (digitsOnly.length < 7 || digitsOnly.length > 15) {
            errors.push('Invalid phone number (must be 7-15 digits)');
          }

          // Check for obviously invalid patterns (all same digit)
          if (digitsOnly.match(/^(\d)\1{6,}$/)) {
            errors.push('Invalid phone number pattern');
          }
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (customer.email && !emailRegex.test(customer.email)) {
          errors.push('Invalid email format');
        }

        if (errors.length > 0) {
          customer.status = 'error';
          customer.errors = errors;
          errorCount++;
        } else {
          customer.status = 'validated';
          validCount++;
        }

        parsedCustomers.push(customer);
      }

      setCustomers(parsedCustomers);
      setValidationResults({
        total: parsedCustomers.length,
        valid: validCount,
        errors: errorCount,
        duplicates: 0,
      });
      setActiveStep(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const handleProcessUpload = async () => {
    if (!uploadedFile) return;

    setLoading(true);
    setActiveStep(3);
    setError(null);

    const newBatch: UploadBatch = {
      id: Date.now().toString(),
      filename: uploadedFile.name,
      uploaded_at: new Date().toISOString().slice(0, 16).replace('T', ' '),
      total_records: customers.length,
      validated_records: customers.filter(c => c.status === 'validated').length,
      error_records: customers.filter(c => c.status === 'error').length,
      status: 'processing',
      processing_progress: 0,
    };

    setUploadBatches(prev => [newBatch, ...prev]);

    try {
      let response;

      if (uploadedFile.name.endsWith('.csv')) {
        // Upload CSV file directly
        response = await customerService.uploadCSV(uploadedFile);

        // Update progress to 50%
        setUploadBatches(prev => prev.map(batch =>
          batch.id === newBatch.id
            ? { ...batch, processing_progress: 50 }
            : batch
        ));
      } else {
        // For other formats, convert to JSON and upload
        const validCustomers = customers
          .filter(c => c.status === 'validated')
          .map(c => ({
            name: c.name,
            phone: c.phone,
            email: c.email,
            vehicle_make: c.vehicle_make,
            vehicle_model: c.vehicle_model,
            vehicle_year: c.vehicle_year,
            last_service_date: c.last_service,
            service_due_date: undefined, // Would need to calculate based on last service
          }));

        response = await customerService.uploadJSON(validCustomers);

        // Update progress to 50%
        setUploadBatches(prev => prev.map(batch =>
          batch.id === newBatch.id
            ? { ...batch, processing_progress: 50 }
            : batch
        ));
      }

      // Final progress update
      setUploadBatches(prev => prev.map(batch =>
        batch.id === newBatch.id
          ? {
              ...batch,
              processing_progress: 100,
              status: 'completed' as const,
              total_records: response.imported + response.failed,
              validated_records: response.imported,
              error_records: response.failed,
            }
          : batch
      ));

      // Reset form
      setActiveStep(0);
      setUploadedFile(null);
      setCustomers([]);
      setValidationResults(null);

      // Show success message if there were no errors
      if (response.failed === 0) {
        setError(null);
      } else {
        setError(`Upload completed with ${response.failed} failed records. Check the upload history for details.`);
      }

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : (typeof err === 'string' ? err : 'Failed to upload customers');
      setError(errorMessage);

      // Mark batch as failed
      setUploadBatches(prev => prev.map(batch =>
        batch.id === newBatch.id
          ? { ...batch, status: 'failed' as const, processing_progress: 0 }
          : batch
      ));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: UploadBatch['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      default: return 'default';
    }
  };

  const renderUploadStep = () => (
    <Card>
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        <Box {...getRootProps()} sx={{
          border: '2px dashed #1a237e',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: loading ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'rgba(26, 35, 126, 0.05)' : 'transparent',
          '&:hover': { bgcolor: loading ? 'transparent' : 'rgba(26, 35, 126, 0.02)' },
          opacity: loading ? 0.5 : 1,
        }}>
          <input {...getInputProps()} disabled={loading} />
          <CloudUpload sx={{ fontSize: 64, color: '#1a237e', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {loading ? 'Processing file...' : isDragActive ? 'Drop the file here' : 'Drag & drop customer file'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Supports CSV, Excel (.xlsx, .xls) files up to 10MB
          </Typography>
          <Button
            variant="contained"
            sx={{ bgcolor: '#1a237e' }}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Choose File'}
          </Button>
          {loading && (
            <LinearProgress sx={{ mt: 2 }} />
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Required CSV Columns:
          </Typography>
          <Grid container spacing={1}>
            {['Customer Name', 'Phone', 'Email'].map((col) => (
              <Grid item key={col}>
                <Chip label={col} size="small" color="primary" />
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Optional CSV Columns:
          </Typography>
          <Grid container spacing={1}>
            {['Dealer', 'Vehicle Model', 'Vehicle Year', 'Mileage at Sale', 'Purchase Year', 'VIN', 'Notes', 'Sale Price'].map((col) => (
              <Grid item key={col}>
                <Chip label={col} size="small" variant="outlined" />
              </Grid>
            ))}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );

  const renderValidationStep = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Validation Results</Typography>
          <Button
            startIcon={<Visibility />}
            onClick={() => setShowPreview(true)}
          >
            Preview Data
          </Button>
        </Box>

        {validationResults && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {validationResults.total}
                </Typography>
                <Typography variant="body2">Total Records</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#4CAF50' }}>
                  {validationResults.valid}
                </Typography>
                <Typography variant="body2">Valid Records</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#f44336' }}>
                  {validationResults.errors}
                </Typography>
                <Typography variant="body2">Error Records</Typography>
              </Paper>
            </Grid>
            <Grid item xs={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: '#ff9800' }}>
                  {validationResults.duplicates}
                </Typography>
                <Typography variant="body2">Duplicates</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {validationResults?.errors > 0 && (
          <Alert
            severity="warning"
            action={
              <Button size="small" onClick={() => setShowErrorDialog(true)}>
                View Errors
              </Button>
            }
          >
            {validationResults.errors} records have validation errors and will be skipped
          </Alert>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={validationResults?.valid === 0 || loading}
            sx={{ bgcolor: '#4CAF50' }}
          >
            Continue with {validationResults?.valid} valid records
          </Button>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(0)}
            disabled={loading}
          >
            Upload Different File
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderConfigStep = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Import Configuration
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadConfig.skipDuplicates}
                  onChange={(e) => setUploadConfig(prev => ({
                    ...prev,
                    skipDuplicates: e.target.checked
                  }))}
                />
              }
              label="Skip duplicate phone numbers"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadConfig.validatePhones}
                  onChange={(e) => setUploadConfig(prev => ({
                    ...prev,
                    validatePhones: e.target.checked
                  }))}
                />
              }
              label="Validate phone number formats"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadConfig.enableGDPRConsent}
                  onChange={(e) => setUploadConfig(prev => ({
                    ...prev,
                    enableGDPRConsent: e.target.checked
                  }))}
                />
              }
              label="Require GDPR consent before messaging"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Default Contact Method</InputLabel>
              <Select
                value={uploadConfig.defaultContactMethod}
                onChange={(e) => setUploadConfig(prev => ({
                  ...prev,
                  defaultContactMethod: e.target.value as any
                }))}
              >
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleProcessUpload}
            disabled={loading}
            sx={{ bgcolor: '#1a237e' }}
          >
            {loading ? 'Processing...' : 'Process & Import'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setActiveStep(1)}
            disabled={loading}
          >
            Back to Validation
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Customer Data Upload
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Import customer data from dealership DMS exports for automated outreach campaigns
      </Typography>

      {/* Stepper */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      {activeStep === 0 && renderUploadStep()}
      {activeStep === 1 && renderValidationStep()}
      {activeStep === 2 && renderConfigStep()}
      {activeStep === 3 && (
        <Card>
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Upload sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Processing Upload
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Importing and validating customer records...
              </Typography>
              <LinearProgress sx={{ mb: 2 }} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Upload History */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload History
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadBatches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>{batch.filename}</TableCell>
                    <TableCell>{batch.uploaded_at}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {batch.total_records} total
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {batch.validated_records} valid, {batch.error_records} errors
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={batch.status}
                        color={getStatusColor(batch.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {batch.status === 'processing' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={batch.processing_progress}
                            sx={{ width: 100 }}
                          />
                          <Typography variant="caption">
                            {batch.processing_progress}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Complete
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => console.log('View batch:', batch)}>
                        <Analytics />
                      </IconButton>
                      <IconButton size="small">
                        <Download />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Data Preview</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.slice(0, 10).map((customer, index) => (
                  <TableRow key={index}>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>
                      {customer.vehicle_make} {customer.vehicle_model} {customer.vehicle_year}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={customer.status}
                        color={customer.status === 'validated' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Validation Errors</DialogTitle>
        <DialogContent>
          <List>
            {customers.filter(c => c.status === 'error').map((customer, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <ErrorIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={customer.name}
                  secondary={customer.errors?.join(', ')}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerUpload;